import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';

/**
 * Responsive Bottom Sheet / Modal component
 * - Mobile: Slide-up bottom sheet
 * - Desktop: Centered modal with max-width
 */
const BottomSheet = ({ isOpen, onClose, title, children, maxHeight = '85vh' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
      document.body.style.overflow = 'hidden';
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 300);
      document.body.style.overflow = '';
      return () => clearTimeout(timer);
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isVisible) return null;

  return (
    <div 
      className={`
        fixed inset-0 z-50 
        flex items-end sm:items-center justify-center
        transition-colors duration-300
        ${isAnimating ? 'bg-black/50' : 'bg-transparent'}
      `}
      onClick={handleBackdropClick}
    >
      {/* Mobile: bottom sheet | Desktop: centered modal */}
      <div 
        className={`
          w-full bg-white shadow-2xl
          flex flex-col
          transition-all duration-300 ease-out
          
          /* Mobile: bottom sheet style */
          rounded-t-2xl
          sm:rounded-2xl
          sm:max-w-lg
          sm:mx-4
          sm:my-8
          
          /* Animation */
          ${isAnimating 
            ? 'translate-y-0 opacity-100 sm:scale-100' 
            : 'translate-y-full opacity-0 sm:translate-y-0 sm:scale-95'
          }
        `}
        style={{ maxHeight }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0 relative">
          {/* Drag handle indicator - mobile only */}
          <div className="absolute top-2 left-1/2 transform -translate-x-1/2 w-10 h-1 bg-gray-300 rounded-full sm:hidden" />
          
          <h2 className="text-lg font-semibold text-gray-900 mt-2 sm:mt-0">
            {title}
          </h2>
          
          <button
            onClick={onClose}
            className="p-2 -mr-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Content - scrollable */}
        <div className="flex-1 overflow-y-auto p-4">
          {children}
        </div>
      </div>
    </div>
  );
};

export default BottomSheet;